import { Component, useEffect, useRef, useState, useCallback } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, type EditorState, type LexicalEditor } from 'lexical';
import { ImageNode } from './editor/ImageNode';
import { ToolbarPlugin } from './editor/ToolbarPlugin';
import { ImagePlugin } from './editor/ImagePlugin';

// ── Simple error boundary for Lexical ──────────────────────────────
class LexicalErrorBoundary extends Component<
  { children: React.ReactNode; onError: (e: Error) => void },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) { this.props.onError(error); }
  render() { return this.state.hasError ? null : this.props.children; }
}

// ── Lexical theme ──────────────────────────────────────────────────
const THEME = {
  paragraph: 'mb-2 leading-relaxed',
  heading: { h2: 'text-[18px] font-700 mt-3 mb-1', h3: 'text-[15px] font-700 mt-2 mb-1' },
  quote: 'border-l-[3px] border-violet-500/50 pl-4 italic text-white/40 my-2',
  list: {
    ul: 'list-disc list-inside space-y-1 mb-2',
    ol: 'list-decimal list-inside space-y-1 mb-2',
    listitem: 'ml-2',
    nested: { listitem: 'ml-4' },
  },
  text: {
    bold: 'font-700',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
    code: 'font-mono bg-white/10 rounded px-1 text-[12px]',
  },
  link: 'text-violet-400 underline cursor-pointer',
};

// ── Plugin: load initial HTML into editor ─────────────────────────
const InitContentPlugin = ({ html }: { html: string }) => {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    const isEmpty = !html || html === '<p><br></p>' || html === '<p></p>' || html.trim() === '';
    if (initialized.current || isEmpty) return;
    initialized.current = true;

    editor.update(() => {
      const root = $getRoot();
      if (root.getTextContent().trim() !== '') return;
      const parser = new DOMParser();
      const dom = parser.parseFromString(html, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      if (nodes.length > 0) {
        root.clear();
        root.append(...nodes);
      }
    });
  }, [editor, html]);

  return null;
};

// ── Plugin: emit HTML + plaintext on every change ─────────────────
const HtmlOutputPlugin = ({ onChange }: { onChange: (html: string, text: string) => void }) => {
  const handleChange = useCallback(
    (editorState: EditorState, ed: LexicalEditor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(ed, null);
        const text = $getRoot().getTextContent();
        onChange(html, text);
      });
    },
    [onChange]
  );

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
};

// ── Main component ────────────────────────────────────────────────
interface RichTextEditorProps {
  value: string;
  onChange: (html: string, plainText: string) => void;
  placeholder?: string;
  minHeight?: number;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Escreva o conteúdo do comunicado...',
  minHeight = 240,
}: RichTextEditorProps) => {
  const [imageOpen, setImageOpen] = useState(false);

  const initialConfig = {
    namespace: 'BroadcastEditor',
    theme: THEME,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode, ImageNode],
    onError: (err: Error) => console.error('[RichEditor]', err),
  };

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden focus-within:border-violet-500/60 transition-colors">
      <LexicalComposer initialConfig={initialConfig}>
        {/* Toolbar */}
        <ToolbarPlugin onImageRequest={() => setImageOpen(true)} />

        {/* Editor area */}
        <div className="relative bg-white/[0.04]" style={{ minHeight }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="outline-none p-4 text-white text-[14px] leading-relaxed"
                style={{ minHeight }}
                spellCheck
              />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-white/20 text-[14px] pointer-events-none select-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />

          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <InitContentPlugin html={value} />
          <HtmlOutputPlugin onChange={onChange} />
        </div>

        {/* Image insert modal */}
        <ImagePlugin open={imageOpen} onClose={() => setImageOpen(false)} />
      </LexicalComposer>
    </div>
  );
};
